import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import type { ApiError, ApiResult } from '@/shared/api/types';
import {
  fetchQuery,
  getSnapshot,
  isFresh,
  retener,
  seedQueryData,
  subscribe,
  type QuerySnapshot,
} from './queryCache';

export {
  clearPrivateQueries,
  clearQueryCache,
  fetchQuery,
  invalidateQueries,
  seedQueryData,
  setQueryData,
} from './queryCache';

/** Tiempo por defecto en que los datos se consideran frescos. */
export const STALE_MS = 60_000;

export interface QueryOptions<T> {
  /** Milisegundos en que los datos son frescos (no se vuelven a pedir al montar ni al enfocar). */
  staleMs?: number;
  /** Revalida cada tanto mientras la pestaña está visible. */
  pollMs?: number;
  /** false = no pide nada (p. ej. hasta tener sesión). */
  enabled?: boolean;
  /** Snapshot persistido: se pinta al instante y se revalida en segundo plano. */
  initialData?: T;
  revalidateOnFocus?: boolean;
  revalidateOnReconnect?: boolean;
  /** Se llama cada vez que llegan datos nuevos de la red (no con la semilla). */
  onSuccess?: (data: T) => void;
}

export interface QueryState<T> {
  data: T | undefined;
  error: ApiError | null;
  /** Cargando por primera vez: no hay datos ni error todavía. */
  isLoading: boolean;
  /** Hay una petición en curso (primera o revalidación). */
  isFetching: boolean;
  /** Los datos vienen del snapshot guardado y aún no se confirmaron con la red. */
  isSnapshot: boolean;
  /** Date.now() de la última respuesta buena (null si solo hay snapshot). */
  updatedAt: number | null;
  /** Vuelve a pedir ahora, aunque estén frescos. null si la consulta está desactivada. */
  refetch: () => Promise<ApiResult<T> | null>;
}

const SIN_CLAVE = '\u0000';

/**
 * Consulta con caché compartida (SWR propio): deduplica peticiones, muestra el snapshot al instante,
 * revalida al enfocar la pestaña y al recuperar la conexión, y conserva los datos viejos si la red falla.
 * `key = null` desactiva la consulta (útil para dependencias que aún no existen).
 */
export function useApiQuery<T>(
  key: string | null,
  fetcher: (signal: AbortSignal) => Promise<ApiResult<T>>,
  options: QueryOptions<T> = {},
): QueryState<T> {
  const { staleMs = STALE_MS, pollMs, enabled = true, initialData, onSuccess } = options;
  const { revalidateOnFocus = true, revalidateOnReconnect = true } = options;
  const activa = enabled && key !== null;
  const clave = key ?? SIN_CLAVE;

  const fetcherRef = useRef(fetcher);
  const onSuccessRef = useRef(onSuccess);
  useEffect(() => {
    fetcherRef.current = fetcher;
    onSuccessRef.current = onSuccess;
  });

  // Idempotente: solo escribe si la clave está vacía, así el primer pintado ya tiene los datos guardados.
  if (activa && initialData !== undefined) seedQueryData(clave, initialData);

  const suscribir = useCallback((aviso: () => void) => subscribe(clave, aviso), [clave]);
  const leer = useCallback(() => getSnapshot<T>(clave), [clave]);
  const snap: QuerySnapshot<T> = useSyncExternalStore(suscribir, leer, leer);

  const pedir = useCallback(
    (force: boolean): Promise<ApiResult<T>> =>
      fetchQuery<T>(clave, (signal) => fetcherRef.current(signal), { staleMs, force }),
    [clave, staleMs],
  );

  // Montaje / cambio de clave: pide si no hay datos frescos.
  useEffect(() => {
    if (!activa) return undefined;
    const soltar = retener(clave, () => void pedir(true));
    if (!isFresh(clave, staleMs)) void pedir(false);
    return soltar;
  }, [activa, clave, staleMs, pedir]);

  // Revalidar al volver a la pestaña o recuperar la red.
  useEffect(() => {
    if (!activa) return undefined;
    const revalidar = (): void => {
      if (document.visibilityState !== 'hidden' && !isFresh(clave, staleMs)) void pedir(false);
    };
    if (revalidateOnFocus) {
      window.addEventListener('focus', revalidar);
      document.addEventListener('visibilitychange', revalidar);
    }
    if (revalidateOnReconnect) window.addEventListener('online', revalidar);
    return () => {
      window.removeEventListener('focus', revalidar);
      document.removeEventListener('visibilitychange', revalidar);
      window.removeEventListener('online', revalidar);
    };
  }, [activa, clave, staleMs, pedir, revalidateOnFocus, revalidateOnReconnect]);

  useEffect(() => {
    if (!activa || !pollMs) return undefined;
    const id = setInterval(() => {
      if (document.visibilityState !== 'hidden') void pedir(true);
    }, pollMs);
    return () => clearInterval(id);
  }, [activa, pollMs, pedir]);

  const ultimaActualizacion = useRef<number | null>(null);
  useEffect(() => {
    if (!activa || snap.updatedAt === null || snap.data === undefined) return;
    if (ultimaActualizacion.current === snap.updatedAt) return;
    ultimaActualizacion.current = snap.updatedAt;
    onSuccessRef.current?.(snap.data);
  }, [activa, snap.updatedAt, snap.data]);

  const refetch = useCallback(
    (): Promise<ApiResult<T> | null> => (activa ? pedir(true) : Promise.resolve(null)),
    [activa, pedir],
  );

  return {
    data: activa ? snap.data : undefined,
    error: activa ? snap.error : null,
    isLoading: activa && snap.data === undefined && snap.error === null,
    isFetching: activa && snap.isFetching,
    isSnapshot: activa && snap.isSeed,
    updatedAt: activa ? snap.updatedAt : null,
    refetch,
  };
}
